<script lang="ts" setup>
import type { IHomeArticle, IHomeHenceCalendar } from '@/utils/types'

const props = defineProps({
  data: {
    type: Object as PropType<{
      recipe: IHomeArticle[]
      calendar: IHomeHenceCalendar
    }>,
    default: () => ({})
  }
})

const DD = computed(() => props.data.calendar ? dateFormat(props.data.calendar.date, 'DD') : '')
const MMMM = computed(() => props.data.calendar ? dateFormat(props.data.calendar.date, 'MMMM', 'en-US') : '')
const dd = computed(() => props.data.calendar ? dateFormat(props.data.calendar.date, 'dd') : '')
const recipeData = computed(() => props.data.recipe?.length > 0 ? props.data.recipe[0] : null)
const link = computed(() => {
  const data = recipeData.value
  if (data?.type === 'articleRecipe') {
    return `/hence/recipe/article/${data?.seo.meta_slug || data?.id}`
  } else {
    return `/hence/article/${data?.seo.meta_slug || data?.id}`
  }
})
</script>

<template>
  <section class="recipe" v-if="data.calendar">
    <Intro subtitle="recipe" :data="{
      title: '跟著節氣做料理'
    }" class="align-left display80 dark recipe__intro" />
    <div class="recipe__calendar mobile">
      <div class="recipe__calendar__top">節氣食令</div>
      <div class="recipe__calendar__middle">
        <div class="recipe__calendar__day">{{ DD }}</div>
        <div class="recipe__calendar__month">{{ MMMM }}</div>
        <div class="recipe__calendar__week">星期{{ dd }}</div>
      </div>
      <div class="recipe__calendar__bottom">{{ data.calendar.solar_terms }}</div>
    </div>
    <figure class="recipe__pic square__pic">
      <Nimg :src="recipeData?.image?.link" :alt="recipeData?.image?.alt" :title="recipeData?.image?.title" />
    </figure>
    <div class="recipe__right">
      <div class="recipe__calendar pc">
        <div class="recipe__calendar__top">節氣食令</div>
        <div class="recipe__calendar__middle">
          <div class="recipe__calendar__day">{{ DD }}</div>
          <div class="recipe__calendar__month">{{ MMMM }}</div>
          <div class="recipe__calendar__week">星期{{ dd }}</div>
        </div>
        <div class="recipe__calendar__bottom">{{ data.calendar.solar_terms }}</div>
      </div>
      <div class="recipe__textBox">
        <nuxt-link :to="link" class="recipe__title">
          {{ recipeData?.subject }}
        </nuxt-link>
        <div class="recipe__text">{{ recipeData?.description }}</div>
        <nuxt-link :to="link" class="recipe__link">
          <IconArrow class="brand4-1" />
        </nuxt-link>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.recipe {
  @include grid;
  padding-bottom: toRem(210);

  @include max-media(767) {
    @include grid(4, 24);
  }

  @include max-media(580) {
    padding-bottom: toRem(48);
  }

  &__intro {
    grid-column: 1 / span 3;

    @include max-media(767) {
      grid-column: 1 / span 4;
    }
  }

  &__pic {
    grid-column: 4 / span 5;

    @include max-media(767) {
      grid-column: 1 / span 3;
    }

    @include max-media(580) {
      grid-column: 1 / span 4;
      margin-bottom: toRem(24);
    }
  }

  &__right {
    display: flex;
    flex-direction: column;
    grid-column: 9 / span 3;

    @include max-media(767) {
      grid-column: 4 / span 1;
    }

    @include max-media(580) {
      grid-column: 1 / span 4;
    }
  }

  &__calendar {
    position: relative;
    width: 100%;
    padding-top: 100%;
    // padding: toRem(24);
    background-color: $color-brand-4_1;
    color: $color-white-1;

    &.mobile {
      grid-column: 1 / span 4;

      @include min-media(581) {
        display: none;
      }
    }

    &.pc {
      @include max-media(580) {
        display: none;
      }
    }

    &__top {
      position: absolute;
      left: 0;
      top: 0;
      padding: 9%;
      // font-size: toRem(20);
      font-size: 1.25vw;
      font-weight: 500;
      line-height: 1.4;

      @include max-media(580) {
        font-size: toRem(20);
        padding: toRem(24);
      }
    }

    &__middle {
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 100%;
      padding: 9%;
      text-align: center;

      @include max-media(580) {
        padding: toRem(24);
      }
    }

    &__day {
      // font-size: toRem(100);
      font-size: 6.3vw;
      font-family: $font-TextaAlt;
      font-weight: 500;
      line-height: 0.8;

      @include max-media(580) {
        font-size: toRem(100);
      }
    }

    &__month {
      margin-bottom: toRem(12);
      // font-size: toRem(32);
      font-size: 2vw;
      font-family: $font-TextaAlt;
      font-weight: 500;
      line-height: 0.8;

      @include max-media(580) {
        font-size: toRem(32);
      }
    }

    &__week {
      // font-size: toRem(20);
      font-size: 1.25vw;
      font-weight: 500;
      line-height: 1.4;

      @include max-media(580) {
        font-size: toRem(20);
      }
    }

    &__bottom {
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      padding: 9%;
      text-align: right;
      // font-size: toRem(32);
      font-size: 2vw;
      font-weight: 500;
      line-height: 1.3;

      @include max-media(580) {
        font-size: toRem(32);
        padding: toRem(24);
      }
    }
  }

  &__textBox {
    margin-top: auto;
  }

  &__title {
    display: block;
    margin-bottom: toRem(12);
    font-size: toRem(20);
    font-weight: 500;
    line-height: 1.4;
  }

  &__text {
    margin-bottom: toRem(12);
    font-size: toRem(16);
    font-weight: 400;
    line-height: 1.45;
    @include dot-many-line(2);
  }
}
</style>
